import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { scroller } from 'react-scroll';
import { Check, X, RotateCcw, Info, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { getMainTranslation } from '../../hooks/useLanguage';
import {
  updateWorkflowSessionState,
  setDeclarationDirty,
  fetchSuggestionsForWorkflow,
  setSelectedSender,
  setSelectedReceiver,
  setSelectedAgent,
} from '../../store/reducers';
import { saveWorkflowSessions, saveActiveWorkflowSessionId } from '../../utils/workflowSessionStorage';
import { setValueAtPath } from '../../utils/dataHelpers';
import { getFieldDisplayName, extractBaseFieldName } from '../../utils/fieldDisplayUtils';
import { getFieldId } from '../../utils/fieldIdUtils';
import { hasValidationErrorsForExport, getPendingByExportExcludingParticipants } from '../../utils/errorCheckUtils';
import { removeFieldErrorImmediately } from '../../utils/validationUtils';

//auto-accept is off
const HIGH_CONFIDENCE_THRESHOLD = 101;

const normalizeConfidenceToPercent = (confidence) => {
  if (confidence == null || confidence === '') return 0;
  const n =
    typeof confidence === 'string'
      ? parseFloat(String(confidence).replace(/%/g, '').trim())
      : Number(confidence);
  if (!Number.isFinite(n)) return 0;
  if (n > 0 && n <= 1) return n * 100;
  return Math.min(100, Math.max(0, n));
};

const formatConfidencePercentDisplay = (confidence) =>
  `${Math.round(normalizeConfidenceToPercent(confidence))}%`;

const PARTICIPANT_FIELDS = new Set(['agente_id', 'cliente_id', 'remitente_id']);

/** Short titles for participant suggestion cards (no "ID" / no technical field path in header). */
const getParticipantRoleLabel = (baseField, isSpanish) => {
  if (baseField === 'remitente_id') return isSpanish ? 'Remitente' : 'Sender';
  if (baseField === 'cliente_id') return isSpanish ? 'Destinatario' : 'Receiver';
  if (baseField === 'agente_id') return isSpanish ? 'Agente' : 'Agent';
  return '';
};

const PARTICIPANT_SCROLL_IDS = {
  agente_id: 'review-agents',
  // remitente_id → exportador / senders (ReviewSenderDetails; Redux selectedReceiver). cliente_id → importador / receivers (ReviewReceiverDetails; Redux selectedSender).
  remitente_id: 'review-sender',
  cliente_id: 'review-receiver',
};

/**
 * Find a participant by id in the list that matches the field.
 */
function findParticipantByFieldAndId(baseField, id, { senders = [], receivers = [], agents = [] }) {
  if (id == null || !PARTICIPANT_FIELDS.has(baseField)) return null;
  const numId = Number(id);
  // Primary list first, then the other (same as review panels / contact picker); toast only if neither has the id.
  if (baseField === 'cliente_id') {
    return (
      receivers.find((c) => Number(c?.id) === numId) ||
      senders.find((c) => Number(c?.id) === numId) ||
      null
    );
  }
  if (baseField === 'remitente_id') {
    return (
      senders.find((c) => Number(c?.id) === numId) ||
      receivers.find((c) => Number(c?.id) === numId) ||
      null
    );
  }
  if (baseField === 'agente_id') {
    return agents.find((c) => Number(c?.id) === numId) || null;
  }
  return null;
}

const PARTICIPANT_PREVIEW_NA = 'NA';

/**
 * Resolved row from Redux lists for participant suggestion cards (ID, nombre, tax line per role).
 */
function getParticipantPreviewForSuggestion(baseField, suggestionValue, { senders = [], receivers = [], agents = [] }, isSpanish) {
  const taxLabelCliente = isSpanish ? 'NIF' : 'NIF';
  const taxLabelRemitente = isSpanish ? 'Identificación fiscal' : 'Tax ID';
  const taxLabelAgente = isSpanish ? 'NIF' : 'NIF';
  const taxLabelForField =
    baseField === 'remitente_id' ? taxLabelRemitente : baseField === 'agente_id' ? taxLabelAgente : taxLabelCliente;

  const emptyOrRemoved =
    suggestionValue === '(leeg)' || suggestionValue === '' || suggestionValue === null || suggestionValue === undefined;
  if (emptyOrRemoved) {
    return {
      idLine: PARTICIPANT_PREVIEW_NA,
      nombreLine: PARTICIPANT_PREVIEW_NA,
      taxLabel: taxLabelForField,
      taxLine: PARTICIPANT_PREVIEW_NA,
    };
  }

  const contact = findParticipantByFieldAndId(baseField, suggestionValue, { senders, receivers, agents });
  const idLine =
    contact?.id != null && String(contact.id).trim() !== ''
      ? String(contact.id)
      : String(suggestionValue).trim() !== ''
        ? String(suggestionValue)
        : PARTICIPANT_PREVIEW_NA;

  const nombreLine =
    contact?.nombre != null && String(contact.nombre).trim() !== '' ? String(contact.nombre) : PARTICIPANT_PREVIEW_NA;

  if (baseField === 'cliente_id') {
    const v = contact?.nif ?? contact?.identificacion_fiscal;
    const taxLine = v != null && String(v).trim() !== '' ? String(v) : PARTICIPANT_PREVIEW_NA;
    return { idLine, nombreLine, taxLabel: taxLabelCliente, taxLine };
  }
  if (baseField === 'remitente_id') {
    const v = contact?.identificacion_fiscal ?? contact?.nif;
    const taxLine = v != null && String(v).trim() !== '' ? String(v) : PARTICIPANT_PREVIEW_NA;
    return { idLine, nombreLine, taxLabel: taxLabelRemitente, taxLine };
  }
  if (baseField === 'agente_id') {
    const v = contact?.nif;
    const taxLine = v != null && String(v).trim() !== '' ? String(v) : PARTICIPANT_PREVIEW_NA;
    return { idLine, nombreLine, taxLabel: taxLabelAgente, taxLine };
  }
  return {
    idLine: PARTICIPANT_PREVIEW_NA,
    nombreLine: PARTICIPANT_PREVIEW_NA,
    taxLabel: '',
    taxLine: PARTICIPANT_PREVIEW_NA,
  };
}

/* Organize suggestions: exports first (declaration + items), then participants as separate top-level section */
const organizeSuggestions = (suggestions) => {
  const participantFields = [];
  const byDecl = {};
  suggestions.forEach((s) => {
    const baseField = extractBaseFieldName(s.fieldPath || '');
    if (s.itemIndex == null && s.docIndex == null && s.embajaIndex == null && PARTICIPANT_FIELDS.has(baseField)) {
      participantFields.push(s);
      return;
    }
    const declIdx = s.declarationIndex ?? 0;
    if (!byDecl[declIdx]) {
      byDecl[declIdx] = {
        declarationIndex: declIdx,
        declarationFields: [],
        itemFields: {}, 
      };
    }
    const decl = byDecl[declIdx];

    if (s.itemIndex == null && s.docIndex == null && s.embajaIndex == null) {
      decl.declarationFields.push(s);
    } else {
      const itemIdx = s.itemIndex ?? 0;
      if (!decl.itemFields[itemIdx]) {
        decl.itemFields[itemIdx] = {
          itemIndex: itemIdx,
          fields: [],
          documentFields: {},
          packageFields: {},
        };
      }
      const item = decl.itemFields[itemIdx];

      if (s.docIndex != null) {
        const docIdx = s.docIndex;
        if (!item.documentFields[docIdx]) item.documentFields[docIdx] = { docIndex: docIdx, fields: [] };
        item.documentFields[docIdx].fields.push(s);
      } else if (s.embajaIndex != null) {
        const embIdx = s.embajaIndex;
        if (!item.packageFields[embIdx]) item.packageFields[embIdx] = { embajaIndex: embIdx, fields: [] };
        item.packageFields[embIdx].fields.push(s);
      } else {
        item.fields.push(s);
      }
    }
  });

  const exportGroups = Object.values(byDecl)
    .sort((a, b) => a.declarationIndex - b.declarationIndex)
    .map((decl) => ({
      ...decl,
      itemFields: Object.values(decl.itemFields)
        .sort((a, b) => a.itemIndex - b.itemIndex)
        .map((item) => ({
          ...item,
          documentFields: Object.values(item.documentFields)
            .sort((a, b) => a.docIndex - b.docIndex),
          packageFields: Object.values(item.packageFields)
            .sort((a, b) => a.embajaIndex - b.embajaIndex),
        })),
    }));
  return { exportGroups, participantFields };
};

/* Compute canonical fieldId from suggestion (fieldPath + indices) to match form input ids */
const computeFieldIdFromSuggestion = (s) => {
  if (s.fieldId) return s.fieldId;
  const declIdx = s.declarationIndex ?? 0;
  const itemIdx = s.itemIndex;
  const docIdx = s.docIndex;
  const embIdx = s.embajaIndex;
  const marcaIdx = s.marcaIndex;
  const baseField = extractBaseFieldName(s.fieldPath || '');
  if (s.fieldPath?.includes('documentos') || s.fieldPath?.includes('.documentos.')) {
    return getFieldId(`documents_${baseField}`, declIdx, itemIdx ?? 0, docIdx ?? 0);
  }
  if (s.fieldPath?.includes('embalajes') && (s.fieldPath?.includes('marca') || baseField === 'marca')) {
    return getFieldId('packages_marcas_marca', declIdx, itemIdx ?? 0, null, embIdx ?? 0, marcaIdx ?? 0);
  }
  if (s.fieldPath?.includes('embalajes')) {
    return getFieldId(`packages_${baseField}`, declIdx, itemIdx ?? 0, null, embIdx ?? 0);
  }
  if (itemIdx != null) {
    return getFieldId(baseField, declIdx, itemIdx);
  }
  return getFieldId(baseField, declIdx);
};

/** Map suggestion to (field, itemIndex, docIndex, embalajeIndex, marcaIndex) for removeFieldErrorImmediately */
const getValidationParamsFromSuggestion = (suggestion) => {
  const baseField = extractBaseFieldName(suggestion.fieldPath || '');
  const itemIdx = suggestion.itemIndex;
  const docIdx = suggestion.docIndex;
  const embIdx = suggestion.embajaIndex;
  const marcaIdx = suggestion.marcaIndex;
  let field = baseField;
  let itemIndex = null;
  let docIndex = null;
  let embalajeIndex = null;
  let marcaIndex = null;
  if (suggestion.fieldPath?.includes('documentos') || suggestion.fieldPath?.includes('.documentos.')) {
    field = `documents_${baseField}`;
    itemIndex = itemIdx ?? 0;
    docIndex = docIdx ?? 0;
  } else if (suggestion.fieldPath?.includes('embalajes') && (suggestion.fieldPath?.includes('marca') || baseField === 'marca')) {
    field = 'marcas_marca';
    itemIndex = itemIdx ?? 0;
    embalajeIndex = embIdx ?? 0;
    marcaIndex = marcaIdx ?? 0;
  } else if (suggestion.fieldPath?.includes('embalajes')) {
    field = `packages_${baseField}`;
    itemIndex = itemIdx ?? 0;
    embalajeIndex = embIdx ?? 0;
  } else if (itemIdx != null) {
    itemIndex = itemIdx;
  }
  return { field, itemIndex, docIndex, embalajeIndex, marcaIndex };
};

/** Patch validation errors by removing the error for the field touched by this suggestion (when value is non-empty). Returns updated errors or null. */
const patchValidationErrorsForSuggestion = (currentErrors, suggestion, value, isSpanish) => {
  if (!currentErrors || !Array.isArray(currentErrors)) return null;
  const declIndex = suggestion.declarationIndex ?? 0;
  const { field, itemIndex, docIndex, embalajeIndex, marcaIndex } = getValidationParamsFromSuggestion(suggestion);
  return removeFieldErrorImmediately(
    currentErrors,
    declIndex,
    field,
    value,
    itemIndex,
    docIndex,
    embalajeIndex,
    marcaIndex,
    isSpanish
  );
};

const getConfidenceBadgeClass = (confidence) => {
  const p = normalizeConfidenceToPercent(confidence);
  if (p >= 80) return 'bg-green-500/90 text-white';
  if (p >= 50) return 'bg-amber-500/90 text-white';
  return 'bg-red-500/90 text-white';
};

const formatValue = (val, isSpanish = false) => {
  if (val === null || val === undefined) return isSpanish ? 'Ninguno' : 'None';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

const hasRevertableOldValue = (s) => s.oldValue !== undefined && s.oldValue !== null;

const AiSuggestionCard = ({
  suggestion,
  isSpanish,
  onAccept,
  onReject,
  onRetry,
  onExpandExport,
  participantLists,
}) => {
  const { fieldPath, type, value, oldValue, confidence, status } = suggestion;
  const fieldId = useMemo(() => computeFieldIdFromSuggestion(suggestion), [suggestion]);
  const isChanged = type === 'changed';
  const isAccepted = status === 'accepted';
  const isRejected = status === 'rejected';
  const baseField = extractBaseFieldName(fieldPath || '');
  const fieldLabel = PARTICIPANT_FIELDS.has(baseField)
    ? getParticipantRoleLabel(baseField, isSpanish)
    : getFieldDisplayName(baseField, isSpanish);

  const participantPreview = useMemo(() => {
    if (!participantLists || !PARTICIPANT_FIELDS.has(baseField)) return null;
    return getParticipantPreviewForSuggestion(baseField, value, participantLists, isSpanish);
  }, [participantLists, baseField, value, isSpanish]);

  const scrollTargetId = useMemo(() => {
    const participantId = PARTICIPANT_SCROLL_IDS[baseField];
    if (participantId) {
      const declIdx = suggestion.declarationIndex ?? 0;
      return `${participantId}-${declIdx}`;
    }
    return fieldId;
  }, [baseField, fieldId, suggestion.declarationIndex]);

  const scrollToField = useCallback((targetId) => {
    const id = targetId || scrollTargetId;
    const element = document.getElementById(id);
    if (!element) return;
    const container = document.getElementById('analysis-report');
    requestAnimationFrame(() => {
      try {
        if (container) {
          scroller.scrollTo(id, {
            duration: 300,
            offset: -120,
            smooth: 'easeInOutQuint',
            containerId: 'analysis-report',
          });
        } else {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }
      } catch {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
      setTimeout(() => {
        element.classList.add('highlight-non-missing');
        setTimeout(() => element.classList.remove('highlight-non-missing'), 3000);
      }, 400);
    });
  }, [scrollTargetId]);

  const handleClick = () => {
    const declIndex = suggestion.declarationIndex ?? 0;
    onExpandExport?.(declIndex);
    scrollToField();
  };

  const handleAccept = (e) => {
    e.stopPropagation();
    onAccept?.(suggestion);
    setTimeout(() => scrollToField(), 150);
  };

  const handleReject = (e) => {
    e.stopPropagation();
    onReject?.(suggestion);
    setTimeout(() => scrollToField(), 150);
  };

  const handleRetry = (e) => {
    e.stopPropagation();
    onRetry?.(suggestion);
    setTimeout(() => scrollToField(), 150);
  };

  const displayValue = formatValue(value, isSpanish);
  const [showTooltip, setShowTooltip] = useState(false);
  const valueRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const handleMouseEnter = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ top: rect.top, left: rect.left });
    setShowTooltip(true);
  }, []);

  const handleMouseLeave = useCallback(() => setShowTooltip(false), []);

  const tooltipContent = isChanged ? (
    <div className="flex flex-col gap-2">
      <div className="text-gray-700 font-semibold text-xs pb-1.5 border-b border-gray-200">
        {fieldLabel}
      </div>
      <div>
        <div className="text-gray-500 text-[10px] font-semibold uppercase tracking-wide">
          {isSpanish ? 'Antiguo' : 'Old'}
        </div>
        <div className="text-gray-800 mt-0.5">{formatValue(oldValue, isSpanish)}</div>
      </div>
      <div>
        <div className="text-gray-500 text-[10px] font-semibold uppercase tracking-wide">
          {isSpanish ? 'Nuevo' : 'New'}
        </div>
        <div className="text-gray-800 mt-0.5">{formatValue(value, isSpanish)}</div>
      </div>
    </div>
  ) : (
    <div className="flex flex-col gap-1">
      <div className="text-gray-700 font-semibold text-xs pb-1.5 border-b border-gray-200">
        {fieldLabel}
      </div>
      <div className="text-gray-500 text-[10px] font-semibold uppercase tracking-wide">
        {isSpanish ? 'Eliminado' : 'Removed'}
      </div>
      <div className="text-gray-800 mt-0.5">{formatValue(value, isSpanish)}</div>
    </div>
  );

  return (
    <div
      ref={valueRef}
      className="p-3 px-3 rounded-lg text-sm break-words bg-secondary cursor-pointer hover:opacity-90 transition-opacity relative"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0 flex gap-1 truncate">
          <span className="font-semibold text-xs break-words text-gray-700">
            {fieldLabel}
          </span>
          {!PARTICIPANT_FIELDS.has(baseField) && (
            <div className="text-[8px] text-gray-500 font-mono break-all truncate bottom-[-0px] relative">
              {extractBaseFieldName(fieldPath)}
            </div>
          )}
        </div>
        <span
          className={`hiddena inline-flex items-center justify-center min-w-[2rem] px-1.5 rounded-full text-[8px] font-semibold flex-shrink-0 ${getConfidenceBadgeClass(confidence)}`}
        >
          {formatConfidencePercentDisplay(confidence)}
        </span>
      </div>
      {participantPreview && participantPreview.taxLabel ? (
        <div className="mt-1 space-y-1 text-[11px] leading-snug text-gray-600 mb-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-xs bg-white px-2.5 py-0.5 text-center rounded-full whitespace-nowrap min-w-[1.75rem] font-medium text-gray-700 shrink-0">
              {participantPreview.idLine}
            </span>
            <span className="min-w-0 break-words font-medium text-gray-800">
              {participantPreview.nombreLine}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-1.5 items-baseline pl-0.5">
            <span className="text-gray-500 font-medium shrink-0">{participantPreview.taxLabel}</span>
            <span className="min-w-0 break-all">{participantPreview.taxLine}</span>
          </div>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 mt-0">
        <div className="text-xs text-gray-600 flex-1 min-w-0 truncate">
        {!participantPreview || !participantPreview?.taxLabel ? displayValue : ""}
        </div>
        {showTooltip &&
          createPortal(
            <div
              className="fixed z-[9999] px-2 py-1.5 text-xs rounded-lg bg-white text-gray-800 shadow-lg whitespace-pre-line max-w-72 w-48 pointer-events-none animate-[fadeIn_0.2s_ease-out]"
              style={{
                top: tooltipPos.top - 8,
                left: tooltipPos.left,
                transform: 'translateY(-100%)',
              }}
            >
              {tooltipContent}
            </div>,
            document.body
          )}
        <div className="flex items-center gap-1 flex-shrink-0">
          {isAccepted && hasRevertableOldValue(suggestion) ? (
            <button
              type="button"
              onClick={handleRetry}
              className="p-1 rounded-full bg-gray-200 bg-white transition-colors"
              title={getMainTranslation('aiSuggestionRetry', isSpanish)}
            >
              <RotateCcw className="size-4" />
            </button>
          ) : isAccepted && !hasRevertableOldValue(suggestion) ? null : !isRejected ? (
            <>
              <button
                type="button"
                onClick={handleReject}
                className="p-1 rounded-full bg-white text-red-600 transition-colors"
                title={getMainTranslation('aiSuggestionReject', isSpanish)}
              >
                <X className="size-4" />
              </button>
              <button
                type="button"
                onClick={handleAccept}
                className="svg-force-white p-1 rounded-full bg-primary text-white hover:bg-primary/90 transition-colors"
                title={getMainTranslation('aiSuggestionAccept', isSpanish)}
              >
                <Check className="size-4" />
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const AiSuggestionsContainer = ({ activeWorkflowSessionId, isSpanish }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [isAutoFilledCollapsed, setIsAutoFilledCollapsed] = useState(true);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [infoTooltipPos, setInfoTooltipPos] = useState({ top: 0, left: 0 });
  /** Export section collapse: key = declarationIndex, value = true when collapsed */
  const [collapsedExportByIndex, setCollapsedExportByIndex] = useState({});
  const [isParticipantsCollapsed, setIsParticipantsCollapsed] = useState(false);

  const handleInfoMouseEnter = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setInfoTooltipPos({ top: rect.bottom + 8, left: rect.left });
    setShowInfoTooltip(true);
  }, []);

  const handleInfoMouseLeave = useCallback(() => setShowInfoTooltip(false), []);

  /** Debounce persist to avoid blocking UI when user clicks Accept/Reject multiple times in quick succession (JSON.stringify + sessionStorage write is synchronous). */
  const PERSIST_DEBOUNCE_MS = 200;
  const persistTimerRef = useRef(null);

  /** Persist workflow sessions to sessionStorage so accepted/reverted data survives refresh. Debounced to avoid repeated heavy serialization on rapid clicks. */
  const persistWorkflowToStorage = useCallback(() => {
    const doPersist = () => {
      const state = store.getState();
      const ws = state?.workflowSessions;
      if (ws?.sessions && ws?.sessionOrder) {
        saveWorkflowSessions(ws.sessions, ws.sessionOrder, ws.activeSessionId, true);
        if (ws.activeSessionId) saveActiveWorkflowSessionId(ws.activeSessionId);
      }
      persistTimerRef.current = null;
    };
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(doPersist, PERSIST_DEBOUNCE_MS);
  }, [store]);

  // Flush pending persist on unmount so last action is not lost
  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
        const state = store.getState();
        const ws = state?.workflowSessions;
        if (ws?.sessions && ws?.sessionOrder) {
          saveWorkflowSessions(ws.sessions, ws.sessionOrder, ws.activeSessionId, true);
          if (ws.activeSessionId) saveActiveWorkflowSessionId(ws.activeSessionId);
        }
      }
    };
  }, [store]);

  const { aiSuggestions, editableData, ocrInputID, validationErrors, isDirty, reviewDetailsExportCollapsedByIndex, suggestionsLoading } = useSelector((state) => {
    const session = activeWorkflowSessionId
      ? state.workflowSessions?.sessions?.[activeWorkflowSessionId]
      : null;
    const suggestions = session?.state?.aiSuggestions ?? [];
    const editable = session?.state?.editableData;
    const editableArray = Array.isArray(editable)
      ? editable
      : editable && typeof editable === 'object'
        ? [editable]
        : [];
    const ocrId = session?.state?.ocrInputID ?? null;
    const validation = session?.state?.validationErrors ?? [];
    const dirty = session?.state?.isDirty ?? false;
    const reviewCollapsed = session?.state?.reviewDetailsExportCollapsedByIndex ?? {};
    const loading = session?.state?.suggestionsLoading ?? false;
    return { aiSuggestions: suggestions, editableData: editableArray, ocrInputID: ocrId, validationErrors: validation, isDirty: dirty, reviewDetailsExportCollapsedByIndex: reviewCollapsed, suggestionsLoading: loading };
  });

  const participantLists = useSelector((state) => ({
    senders: state.senders || [],
    receivers: state.receivers || [],
    agents: state.agents || [],
  }));

  // Fetch suggestions from API when ocrInputID is available
  useEffect(() => {
    if (!activeWorkflowSessionId || !ocrInputID) return;
    dispatch(fetchSuggestionsForWorkflow(ocrInputID, activeWorkflowSessionId));
  }, [activeWorkflowSessionId, ocrInputID, dispatch]);

  // Collapse export sections in DeclarationSummary only when user clicks "Save changes" and there are no errors left for that export.
  // Run collapse logic only when isDirty transitions from true → false (save just happened), not on every validationErrors/editableData change.
  const prevIsDirtyForCollapseRef = useRef(isDirty);
  useEffect(() => {
    if (!activeWorkflowSessionId) return;
    const justSaved = prevIsDirtyForCollapseRef.current === true && isDirty === false;
    prevIsDirtyForCollapseRef.current = isDirty;
    if (isDirty || !justSaved) return;

    const fromSuggestions = (aiSuggestions || []).map((s) => s.declarationIndex ?? 0);
    const fromEditable = (editableData || []).length ? Array.from({ length: editableData.length }, (_, i) => i) : [];
    const fromValidation = (validationErrors || []).length ? Array.from({ length: validationErrors.length }, (_, i) => i) : [];
    const exportIndices = [...new Set([...fromSuggestions, ...fromEditable, ...fromValidation])];

    // Declaration Summary collapse: only consider export-section pending (exclude participants; those drive Review Details)
    const pendingByExport = getPendingByExportExcludingParticipants(aiSuggestions);

    const state = store.getState();
    const session = state.workflowSessions?.sessions?.[activeWorkflowSessionId];
    const current = session?.state?.declarationSummaryCollapsedByIndex || {};
    let updated = false;
    const next = { ...current };
    exportIndices.forEach((idx) => {
      const noPending = (pendingByExport[idx] ?? 0) === 0;
      const noValidationErrors = !hasValidationErrorsForExport(validationErrors, idx);
      if (noPending && noValidationErrors && !next[idx]) {
        next[idx] = true;
        updated = true;
      }
    });
    if (updated) {
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { declarationSummaryCollapsedByIndex: next }));
    }
  }, [activeWorkflowSessionId, aiSuggestions, editableData, validationErrors, isDirty, dispatch, store]);

  // Auto-collapse Review Details export when that export has no pending participant (agent/receiver/sender) suggestions
  useEffect(() => {
    if (!activeWorkflowSessionId) return;
    const participantSuggestions = (aiSuggestions || []).filter((s) => {
      const baseField = extractBaseFieldName(s.fieldPath || '');
      return PARTICIPANT_FIELDS.has(baseField);
    });
    const pendingParticipantByExport = {};
    participantSuggestions.forEach((s) => {
      const idx = s.declarationIndex ?? 0;
      const isPending = s.status !== 'accepted' && s.status !== 'rejected';
      if (isPending) pendingParticipantByExport[idx] = (pendingParticipantByExport[idx] || 0) + 1;
    });
    const exportIndicesWithParticipants = [...new Set(participantSuggestions.map((s) => s.declarationIndex ?? 0))];
    const state = store.getState();
    const session = state.workflowSessions?.sessions?.[activeWorkflowSessionId];
    const current = session?.state?.reviewDetailsExportCollapsedByIndex || {};
    let updated = false;
    const next = { ...current };
    exportIndicesWithParticipants.forEach((idx) => {
      if ((pendingParticipantByExport[idx] ?? 0) === 0 && !next[idx]) {
        next[idx] = true;
        updated = true;
      }
    });
    if (updated) {
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { reviewDetailsExportCollapsedByIndex: next }));
    }
  }, [activeWorkflowSessionId, aiSuggestions, dispatch, store]);

  const toggleReviewDetailsExportCollapsed = useCallback(
    (declIndex) => {
      if (!activeWorkflowSessionId) return;
      const next = { ...reviewDetailsExportCollapsedByIndex, [declIndex]: !reviewDetailsExportCollapsedByIndex[declIndex] };
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { reviewDetailsExportCollapsedByIndex: next }));
    },
    [activeWorkflowSessionId, reviewDetailsExportCollapsedByIndex, dispatch]
  );

  const handleAccept = useCallback(
    (suggestion) => {
      if (!activeWorkflowSessionId) return;

      const state = store.getState();
      const session = state.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const currentEditable = session?.state?.editableData;
      const editableArray = Array.isArray(currentEditable)
        ? currentEditable
        : currentEditable && typeof currentEditable === 'object'
          ? [currentEditable]
          : [];
      if (!editableArray.length) return;

      const declIndex = suggestion.declarationIndex ?? 0;
      const declaration = editableArray[declIndex];
      if (!declaration) return;

      const valueToSet =
        suggestion.value === '(leeg)' || suggestion.value === ''
          ? null
          : suggestion.value;
      const updatedDeclaration = setValueAtPath(
        declaration,
        suggestion.fieldPath,
        valueToSet
      );
      const newEditableData = editableArray.map((d, i) =>
        i === declIndex ? updatedDeclaration : d
      );

      const currentSuggestions = session?.state?.aiSuggestions ?? aiSuggestions ?? [];
      const updatedSuggestions = (Array.isArray(currentSuggestions) ? currentSuggestions : []).map((s) =>
        s.id === suggestion.id ? { ...s, status: 'accepted' } : s
      );

      const sessionUpdate = {
        editableData: newEditableData,
        aiSuggestions: updatedSuggestions,
        isDirty: true,
        declarationSummaryCollapsedByIndex: { ...(session?.state?.declarationSummaryCollapsedByIndex || {}), [declIndex]: false },
      };

      // Reflect accepted value in validation errors immediately (remove error for this field if it was missing)
      const currentValidationErrors = session?.state?.validationErrors ?? [];
      const patchedErrors = patchValidationErrorsForSuggestion(currentValidationErrors, suggestion, valueToSet, isSpanish);
      if (patchedErrors !== null) sessionUpdate.validationErrors = patchedErrors;

      // When accepting a participant suggestion, keep review panel mapping:
      // remitente_id -> selectedSender, cliente_id -> selectedReceiver, agente_id -> selectedAgent.
      const baseField = extractBaseFieldName(suggestion.fieldPath || '');
      if (valueToSet != null && PARTICIPANT_FIELDS.has(baseField)) {
        const senders = state.senders || [];
        const receivers = state.receivers || [];
        const agents = state.agents || [];
        const participant = findParticipantByFieldAndId(baseField, valueToSet, { senders, receivers, agents });
        if (participant) {
          const currentSenders = [...(session?.state?.selectedSender || [])];
          const currentReceivers = [...(session?.state?.selectedReceiver || [])];
          const currentAgents = [...(session?.state?.selectedAgent || [])];
          while (currentSenders.length <= declIndex) currentSenders.push(null);
          while (currentReceivers.length <= declIndex) currentReceivers.push(null);
          while (currentAgents.length <= declIndex) currentAgents.push(null);
          if (baseField === 'remitente_id') {
            currentSenders[declIndex] = participant;
            sessionUpdate.selectedSender = currentSenders;
            dispatch(setSelectedSender(participant, declIndex));
          } else if (baseField === 'cliente_id') {
            currentReceivers[declIndex] = participant;
            sessionUpdate.selectedReceiver = currentReceivers;
            dispatch(setSelectedReceiver(participant, declIndex));
          } else if (baseField === 'agente_id') {
            currentAgents[declIndex] = participant;
            sessionUpdate.selectedAgent = currentAgents;
            dispatch(setSelectedAgent(participant, declIndex));
          }
        } else {
          const fieldLabel = getFieldDisplayName(baseField, isSpanish);
          toast.error(
            getMainTranslation('aiSuggestionParticipantIdNotFound', isSpanish, fieldLabel, valueToSet)
          );
        }
      }

      dispatch(setDeclarationDirty(true));
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, sessionUpdate));
      persistWorkflowToStorage();
    },
    [activeWorkflowSessionId, aiSuggestions, dispatch, store, persistWorkflowToStorage, isSpanish]
  );

  const handleAcceptAll = useCallback(
    (suggestions) => {
      if (!activeWorkflowSessionId || !suggestions?.length) return;

      const state = store.getState();
      const session = state.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const currentEditable = session?.state?.editableData;
      let editableArray = Array.isArray(currentEditable)
        ? [...currentEditable]
        : currentEditable && typeof currentEditable === 'object'
          ? [{ ...currentEditable }]
          : [];
      if (!editableArray.length) return;

      const idsToAccept = new Set(suggestions.map((s) => s.id));
      suggestions.forEach((suggestion) => {
        if (suggestion.status === 'accepted') return;
        const declIndex = suggestion.declarationIndex ?? 0;
        const declaration = editableArray[declIndex];
        if (!declaration) return;
        const valueToSet =
          suggestion.value === '(leeg)' || suggestion.value === ''
            ? null
            : suggestion.value;
        const updated = setValueAtPath(declaration, suggestion.fieldPath, valueToSet);
        editableArray = editableArray.map((d, i) => (i === declIndex ? updated : d));
      });

      const currentSuggestions = session?.state?.aiSuggestions ?? aiSuggestions ?? [];
      const updatedSuggestions = (Array.isArray(currentSuggestions) ? currentSuggestions : []).map((s) =>
        idsToAccept.has(s.id) ? { ...s, status: 'accepted' } : s
      );

      // Collapse in Declaration Summary only when no pending in export section (exclude participants; those drive Review Details)
      const pendingByExportAfter = getPendingByExportExcludingParticipants(updatedSuggestions);
      const exportIndicesInBatch = [...new Set(suggestions.map((s) => s.declarationIndex ?? 0))];
      const sessionValidationErrors = session?.state?.validationErrors ?? [];
      // Reflect all accepted values in validation errors immediately (remove errors for accepted fields)
      let patchedValidationErrors = sessionValidationErrors;
      suggestions.forEach((s) => {
        if (s.status === 'accepted') return;
        const valueToSet = s.value === '(leeg)' || s.value === '' ? null : s.value;
        const next = patchValidationErrorsForSuggestion(patchedValidationErrors, s, valueToSet, isSpanish);
        if (next !== null) patchedValidationErrors = next;
      });

      const collapsedByIndex = { ...(session?.state?.declarationSummaryCollapsedByIndex || {}) };
      exportIndicesInBatch.forEach((idx) => {
        const noPending = (pendingByExportAfter[idx] ?? 0) === 0;
        const noValidationErrors = !hasValidationErrorsForExport(patchedValidationErrors, idx);
        if (noPending && noValidationErrors) collapsedByIndex[idx] = true;
      });

      // DEBUG: collapse state when Accept all is clicked
      console.log('[AiSuggestions Accept All] pendingByExportAfter', pendingByExportAfter);
      console.log('[AiSuggestions Accept All] exportIndicesInBatch', exportIndicesInBatch);
      console.log('[AiSuggestions Accept All] collapsedByIndex (will dispatch)', collapsedByIndex);
      console.log('[AiSuggestions Accept All] session.state.declarationSummaryCollapsedByIndex (before)', session?.state?.declarationSummaryCollapsedByIndex);

      const sessionUpdate = {
        editableData: editableArray,
        aiSuggestions: updatedSuggestions,
        isDirty: true,
        declarationSummaryCollapsedByIndex: collapsedByIndex,
        validationErrors: patchedValidationErrors,
      };

      const senders = state.senders || [];
      const receivers = state.receivers || [];
      const agents = state.agents || [];
      let currentSenders = [...(session?.state?.selectedSender || [])];
      let currentReceivers = [...(session?.state?.selectedReceiver || [])];
      let currentAgents = [...(session?.state?.selectedAgent || [])];
      const missingParticipantAccepts = [];
      suggestions.forEach((suggestion) => {
        if (suggestion.status === 'accepted') return;
        const declIndex = suggestion.declarationIndex ?? 0;
        const valueToSet =
          suggestion.value === '(leeg)' || suggestion.value === '' ? null : suggestion.value;
        const baseField = extractBaseFieldName(suggestion.fieldPath || '');
        if (valueToSet == null || !PARTICIPANT_FIELDS.has(baseField)) return;
        const participant = findParticipantByFieldAndId(baseField, valueToSet, { senders, receivers, agents });
        if (!participant) {
          missingParticipantAccepts.push({ baseField, id: valueToSet });
          return;
        }
        while (currentSenders.length <= declIndex) currentSenders.push(null);
        while (currentReceivers.length <= declIndex) currentReceivers.push(null);
        while (currentAgents.length <= declIndex) currentAgents.push(null);
        if (baseField === 'remitente_id') {
          currentSenders[declIndex] = participant;
          dispatch(setSelectedSender(participant, declIndex));
        } else if (baseField === 'cliente_id') {
          currentReceivers[declIndex] = participant;
          dispatch(setSelectedReceiver(participant, declIndex));
        } else if (baseField === 'agente_id') {
          currentAgents[declIndex] = participant;
          dispatch(setSelectedAgent(participant, declIndex));
        }
      });
      if (missingParticipantAccepts.length === 1) {
        const { baseField, id } = missingParticipantAccepts[0];
        toast.error(
          getMainTranslation(
            'aiSuggestionParticipantIdNotFound',
            isSpanish,
            getFieldDisplayName(baseField, isSpanish),
            id
          )
        );
      } else if (missingParticipantAccepts.length > 1) {
        toast.error(
          getMainTranslation('aiSuggestionParticipantIdNotFoundBatch', isSpanish, missingParticipantAccepts.length)
        );
      }
      sessionUpdate.selectedSender = currentSenders;
      sessionUpdate.selectedReceiver = currentReceivers;
      sessionUpdate.selectedAgent = currentAgents;

      dispatch(setDeclarationDirty(true));
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, sessionUpdate));
      console.log('[AiSuggestions Accept All] dispatched sessionUpdate.declarationSummaryCollapsedByIndex', sessionUpdate.declarationSummaryCollapsedByIndex);
      persistWorkflowToStorage();
    },
    [activeWorkflowSessionId, aiSuggestions, dispatch, store, persistWorkflowToStorage, isSpanish]
  );

  const handleRetryAll = useCallback(
    (suggestions) => {
      if (!activeWorkflowSessionId || !suggestions?.length) return;

      const state = store.getState();
      const session = state.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const currentEditable = session?.state?.editableData;
      let editableArray = Array.isArray(currentEditable)
        ? [...currentEditable]
        : currentEditable && typeof currentEditable === 'object'
          ? [{ ...currentEditable }]
          : [];
      if (!editableArray.length) return;

      const idsToRevert = new Set(suggestions.filter((s) => s.status === 'accepted').map((s) => s.id));
      suggestions.forEach((suggestion) => {
        if (suggestion.status !== 'accepted') return;
        const declIndex = suggestion.declarationIndex ?? 0;
        const declaration = editableArray[declIndex];
        if (!declaration) return;
        const valueToRevert = suggestion.oldValue;
        const updated = setValueAtPath(declaration, suggestion.fieldPath, valueToRevert);
        editableArray = editableArray.map((d, i) => (i === declIndex ? updated : d));
      });

      const currentSuggestions = session?.state?.aiSuggestions ?? aiSuggestions ?? [];
      const updatedSuggestions = (Array.isArray(currentSuggestions) ? currentSuggestions : []).map((s) =>
        idsToRevert.has(s.id) ? { ...s, status: 'pending' } : s
      );

      let patchedValidationErrors = session?.state?.validationErrors ?? [];
      suggestions.forEach((s) => {
        if (!idsToRevert.has(s.id)) return;
        const valueToRevert = s.oldValue;
        const next = patchValidationErrorsForSuggestion(patchedValidationErrors, s, valueToRevert, isSpanish);
        if (next !== null) patchedValidationErrors = next;
      });

      dispatch(setDeclarationDirty(true));
      dispatch(
        updateWorkflowSessionState(activeWorkflowSessionId, {
          editableData: editableArray,
          aiSuggestions: updatedSuggestions,
          isDirty: true,
          validationErrors: patchedValidationErrors,
        })
      );
      persistWorkflowToStorage();
    },
    [activeWorkflowSessionId, aiSuggestions, dispatch, store, persistWorkflowToStorage, isSpanish]
  );

  const handleReject = useCallback(
    (suggestion) => {
      if (!activeWorkflowSessionId) return;

      const state = store.getState();
      const session = state.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const declIndex = suggestion.declarationIndex ?? 0;
      const collapsedByIndex = { ...(session?.state?.declarationSummaryCollapsedByIndex || {}), [declIndex]: false };

      const updatedSuggestions = (aiSuggestions || []).map((s) =>
        s.id === suggestion.id ? { ...s, status: 'rejected' } : s
      );

      dispatch(
        updateWorkflowSessionState(activeWorkflowSessionId, {
          aiSuggestions: updatedSuggestions,
          declarationSummaryCollapsedByIndex: collapsedByIndex,
        })
      );
      persistWorkflowToStorage();
    },
    [activeWorkflowSessionId, aiSuggestions, dispatch, store, persistWorkflowToStorage]
  );

  const handleRetry = useCallback(
    (suggestion) => {
      if (!activeWorkflowSessionId) return;

      const state = store.getState();
      const session = state.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const currentEditable = session?.state?.editableData;
      const editableArray = Array.isArray(currentEditable)
        ? currentEditable
        : currentEditable && typeof currentEditable === 'object'
          ? [currentEditable]
          : [];
      if (!editableArray.length) return;

      const declIndex = suggestion.declarationIndex ?? 0;
      const declaration = editableArray[declIndex];
      if (!declaration) return;

      const valueToRevert = suggestion.oldValue;
      const updatedDeclaration = setValueAtPath(
        declaration,
        suggestion.fieldPath,
        valueToRevert
      );
      const newEditableData = editableArray.map((d, i) =>
        i === declIndex ? updatedDeclaration : d
      );

      const currentSuggestions = session?.state?.aiSuggestions ?? aiSuggestions ?? [];
      const updatedSuggestions = (Array.isArray(currentSuggestions) ? currentSuggestions : []).map((s) =>
        s.id === suggestion.id ? { ...s, status: 'pending' } : s
      );

      const collapsedByIndex = { ...(session?.state?.declarationSummaryCollapsedByIndex || {}), [declIndex]: false };

      const sessionUpdate = {
        editableData: newEditableData,
        aiSuggestions: updatedSuggestions,
        isDirty: true,
        declarationSummaryCollapsedByIndex: collapsedByIndex,
      };
      const currentValidationErrors = session?.state?.validationErrors ?? [];
      const patchedErrors = patchValidationErrorsForSuggestion(currentValidationErrors, suggestion, valueToRevert, isSpanish);
      if (patchedErrors !== null) sessionUpdate.validationErrors = patchedErrors;

      dispatch(setDeclarationDirty(true));
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, sessionUpdate));
      persistWorkflowToStorage();
    },
    [activeWorkflowSessionId, aiSuggestions, dispatch, store, persistWorkflowToStorage, isSpanish]
  );

  // Auto-accept high-confidence (>95%) suggestions when they appear
  useEffect(() => {
    if (!activeWorkflowSessionId || !editableData?.length) return;
    const pending = (aiSuggestions || []).filter(
      (s) =>
        s.status !== 'rejected' &&
        s.status !== 'accepted' &&
        normalizeConfidenceToPercent(s.confidence) > HIGH_CONFIDENCE_THRESHOLD
    );
    if (pending.length > 0) {
      handleAcceptAll(pending);
    }
  }, [aiSuggestions, activeWorkflowSessionId, editableData, handleAcceptAll]);

  const visibleSuggestions = (aiSuggestions || []).filter(
    (s) => s.status !== 'rejected'
  );

  const organizedGroups = useMemo(() => {
    const forExportsAndParticipants = visibleSuggestions.filter(
      (s) => normalizeConfidenceToPercent(s.confidence) <= HIGH_CONFIDENCE_THRESHOLD
    );
    const normalized = forExportsAndParticipants.map((s) => {
      let itemIdx = s.itemIndex;
      let docIdx = s.docIndex;
      let embIdx = s.embajaIndex;
      let marcaIdx = s.marcaIndex;
      if (itemIdx == null || docIdx == null || embIdx == null || (s.fieldPath?.includes('marcas') && marcaIdx == null)) {
        const partidasMatch = s.fieldPath?.match(/partidas\[(\d+)\]/);
        const docMatch = s.fieldPath?.match(/documentos\[(\d+)\]/);
        const embMatch = s.fieldPath?.match(/embalajes\[(\d+)\]/);
        const marcasMatch = s.fieldPath?.match(/marcas\[(\d+)\]/);
        if (itemIdx == null && partidasMatch) itemIdx = parseInt(partidasMatch[1], 10);
        if (docIdx == null && docMatch) docIdx = parseInt(docMatch[1], 10);
        if (embIdx == null && embMatch) embIdx = parseInt(embMatch[1], 10);
        if (marcaIdx == null && marcasMatch) marcaIdx = parseInt(marcasMatch[1], 10);
      }
      return {
        ...s,
        declarationIndex: s.declarationIndex ?? 0,
        itemIndex: itemIdx,
        docIndex: docIdx,
        embajaIndex: embIdx,
        marcaIndex: marcaIdx,
      };
    });
    return organizeSuggestions(normalized);
  }, [visibleSuggestions]);

  const highConfidenceSuggestions = useMemo(
    () =>
      visibleSuggestions.filter((s) => normalizeConfidenceToPercent(s.confidence) > HIGH_CONFIDENCE_THRESHOLD),
    [visibleSuggestions]
  );
  const highConfidenceAccepted = highConfidenceSuggestions.filter((s) => s.status === 'accepted');

  const highConfidenceByExport = useMemo(() => {
    const byExport = {};
    highConfidenceSuggestions.forEach((s) => {
      const idx = s.declarationIndex ?? 0;
      if (!byExport[idx]) byExport[idx] = [];
      byExport[idx].push(s);
    });
    return Object.entries(byExport)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([declIndex, list]) => ({ declarationIndex: Number(declIndex), suggestions: list }));
  }, [highConfidenceSuggestions]);

  const hasSuggestionsBelowThreshold = useMemo(
    () =>
      visibleSuggestions.some((s) => normalizeConfidenceToPercent(s.confidence) <= HIGH_CONFIDENCE_THRESHOLD),
    [visibleSuggestions]
  );

  useEffect(() => {
    if (highConfidenceSuggestions.length > 0 && !hasSuggestionsBelowThreshold) {
      setIsAutoFilledCollapsed(false);
    }
  }, [highConfidenceSuggestions.length, hasSuggestionsBelowThreshold]);

  const getAllSuggestionsFromExport = useCallback((decl) => {
    const out = [...(decl.declarationFields || [])];
    (decl.itemFields || []).forEach((item) => {
      out.push(...(item.fields || []));
      (item.documentFields || []).forEach((doc) => out.push(...(doc.fields || [])));
      (item.packageFields || []).forEach((pkg) => out.push(...(pkg.fields || [])));
    });
    return out;
  }, []);

  const getPendingFromExport = (decl) =>
    getAllSuggestionsFromExport(decl).filter((s) => s.status !== 'accepted' && s.status !== 'rejected');
  const getAcceptedFromExport = (decl) =>
    getAllSuggestionsFromExport(decl).filter((s) => s.status === 'accepted');

  const expandExportInDeclarationSummary = useCallback(
    (declIndex) => {
      if (!activeWorkflowSessionId) return;
      const state = store.getState();
      const session = state.workflowSessions?.sessions?.[activeWorkflowSessionId];
      const next = { ...(session?.state?.declarationSummaryCollapsedByIndex || {}), [declIndex]: false };
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { declarationSummaryCollapsedByIndex: next }));
    },
    [activeWorkflowSessionId, dispatch, store]
  );

  const renderSuggestionGroup = (fields, onExpandExport = expandExportInDeclarationSummary) => {
    if (!fields?.length) return null;
    return (
      <div className="space-y-2 mb-2">
        {fields.map((suggestion) => (
          <AiSuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            isSpanish={isSpanish}
            onAccept={handleAccept}
            onReject={handleReject}
            onRetry={handleRetry}
            onExpandExport={onExpandExport}
            participantLists={participantLists}
          />
        ))}
      </div>
    );  
  };

  if (visibleSuggestions.length === 0) {
    return (
      <p className="ml-4 text-gray-500 dark:text-gray-400 text-sm">
        {isSpanish ? 'No hay sugerencias por ahora' : 'No suggestions at the moment'}
      </p>
    );
  }

  const { exportGroups, participantFields } = organizedGroups;

  return (
    <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-2">
      {/* Auto-filled: suggestions with confidence > 95% */}
      {highConfidenceSuggestions.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between gap-2 mb-2 ml-3">
            <h3 className="font-semibold text-base text-gray-700 dark:text-gray-300 truncate" title={isSpanish ? 'Relleno automático' : 'Auto-filled'}>
              {isSpanish ? 'Relleno automático' : 'Auto-filled'}
            </h3>
            <div className="flex items-center gap-1">
              {highConfidenceAccepted.some(hasRevertableOldValue) && (
                <button
                  type="button"
                  onClick={() => handleRetryAll(highConfidenceSuggestions)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors truncate"
                  title={isSpanish ? 'Revertir todo' : 'Revert all'}
                >
                  <RotateCcw className="size-3.5" />
                  <span>{isSpanish ? 'Revertir todo' : 'Revert all'}</span>
                </button>
              )}
              <button
                type="button"
                className="flex items-center justify-center size-6 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
                onMouseEnter={handleInfoMouseEnter}
                onMouseLeave={handleInfoMouseLeave}
              >
                <Info className="size-4" />
                {showInfoTooltip &&
                  createPortal(
                    <div
                      className="fixed z-[9999] p-2 text-xs rounded-lg bg-white text-gray-800 shadow-lg max-w-56 pointer-events-none animate-[fadeIn_0.2s_ease-out]"
                      style={{
                        top: infoTooltipPos.top,
                        right: '3rem',
                      }}
                    >
                      {isSpanish
                        ? 'Sugerencias de alta confianza aplicadas automáticamente. Revisa o revierte aquí.'
                        : 'High-confidence suggestions applied automatically. Review or revert here.'}
                    </div>,
                    document.body
                  )}
              </button>
              <button
                type="button"
                onClick={() => setIsAutoFilledCollapsed((prev) => !prev)}
                className="flex items-center justify-center size-6 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0 w-fit"
                title={isAutoFilledCollapsed ? (isSpanish ? 'Expandir' : 'Expand') : (isSpanish ? 'Colapsar' : 'Collapse')}
              >
                <ChevronDown className={`size-4 transition-transform ${isAutoFilledCollapsed ? '-rotate-90' : ''}`} />
              </button>
            </div>
          </div>
          {!isAutoFilledCollapsed && (
            <div className="space-y-4">
              {highConfidenceByExport.map(({ declarationIndex, suggestions }) => (
                <div key={declarationIndex} className="mb-3">
                  <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2 ml-3">
                    {isSpanish ? 'Expediente' : 'Export'} #{declarationIndex + 1}
                  </h4>
                  {renderSuggestionGroup(suggestions)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Exports (declaration fields + items) */}
      {exportGroups.map((decl) => {
        const hasAny =
          decl.declarationFields?.length > 0 ||
          decl.itemFields?.some(
            (item) =>
              item.fields?.length > 0 ||
              item.documentFields?.some((d) => d.fields?.length > 0) ||
              item.packageFields?.some((p) => p.fields?.length > 0)
          );
        if (!hasAny) return null;

        const exportPending = getPendingFromExport(decl);
        const exportAccepted = getAcceptedFromExport(decl);
        const exportAll = getAllSuggestionsFromExport(decl);
        const isExportCollapsed = collapsedExportByIndex[decl.declarationIndex] === true;
        const toggleExportCollapsed = () =>
          setCollapsedExportByIndex((prev) => ({ ...prev, [decl.declarationIndex]: !prev[decl.declarationIndex] }));
        const collapseThisExport = () =>
          setCollapsedExportByIndex((prev) => ({ ...prev, [decl.declarationIndex]: true }));
        return (
          <div key={decl.declarationIndex} className="mb-4">
            <div className="flex items-center justify-between gap-2 mb-2 ml-3">
              <h3 className="font-semibold text-base text-gray-700 dark:text-gray-300 truncate min-w-0 flex-1"
                title={`${isSpanish ? 'Expediente' : 'Export'} #${decl.declarationIndex + 1}`}
              >
                {isSpanish ? 'Exp...' : 'Export'} #{decl.declarationIndex + 1}
              </h3>
              <div className="flex items-center gap-1 flex-shrink-0">
              {exportPending.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    handleAcceptAll(exportPending);
                    collapseThisExport();
                  }}
                  className="svg-force-white flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
                  title={isSpanish ? 'Aceptar todo' : 'Accept all'}
                >
                  <Check className="size-3.5" />
                  <p className="truncate">
                  {isSpanish ? 'Aceptar todo' : 'Accept all'}
                  </p>
                </button>
              )}
              {exportAccepted.some(hasRevertableOldValue) && (
                <button
                  type="button"
                  onClick={() => {
                    handleRetryAll(exportAll);
                    collapseThisExport();
                  }}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors ${exportPending.length > 0 ? '' : 'truncate'}`}
                  title={isSpanish ? 'Revertir todo' : 'Revert all'}
                >
                  <RotateCcw className="size-3.5" />
                  {exportPending.length === 0 && (
                    <p className="truncate">{isSpanish ? 'Revertir todo' : 'Revert all'}</p>
                  )}
                </button>
              )}
                <button
                  type="button"
                  onClick={toggleExportCollapsed}
                  className="flex items-center justify-center size-6 rounded-full text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 w-fit"
                  title={isExportCollapsed ? (isSpanish ? 'Expandir' : 'Expand') : (isSpanish ? 'Colapsar' : 'Collapse')}
                >
                  <ChevronDown className={`size-4 transition-transform ${isExportCollapsed ? '-rotate-90' : ''}`} />
                </button>
              </div>
            </div>

            {!isExportCollapsed && (
            <>
            {renderSuggestionGroup(decl.declarationFields)}

            {decl.itemFields?.map((item) => {
              const hasItemData =
                item.fields?.length > 0 ||
                item.documentFields?.some((d) => d.fields?.length > 0) ||
                item.packageFields?.some((p) => p.fields?.length > 0);
              if (!hasItemData) return null;

              return (
                <div key={item.itemIndex} className="ml-2 mb-3">
                  <h4 className="font-medium text-sm mb-2 text-gray-600 dark:text-gray-400 ml-3">
                    {isSpanish ? 'Partida' : 'Item'} #{item.itemIndex + 1}
                  </h4>

                  {renderSuggestionGroup(item.fields)}

                  {item.documentFields?.length > 0 && (
                    <div className="ml-2 mb-2">
                      <h5 className="font-medium text-sm mb-1 text-gray-500 ml-5">
                        {isSpanish ? 'Documentos' : 'Documents'}
                      </h5>
                      {item.documentFields.map((doc) => (
                        <div key={doc.docIndex} className="ml-2 mb-2">
                          <p className="text-xs font-medium mb-1 text-gray-500 ml-3">
                            {isSpanish ? 'Documento' : 'Document'} #{doc.docIndex + 1}
                          </p>
                          {renderSuggestionGroup(doc.fields)}
                        </div>
                      ))}
                    </div>
                  )}

                  {item.packageFields?.length > 0 && (
                    <div className="ml-2 mb-2">
                      <h5 className="font-medium text-sm mb-1 text-gray-500 ml-5">
                        {isSpanish ? 'Embalajes' : 'Packages'}
                      </h5>
                      {item.packageFields.map((pkg) => (
                        <div key={pkg.embajaIndex} className="ml-2 mb-2">
                          <p className="text-xs font-medium mb-1 text-gray-500 ml-3">
                            {isSpanish ? 'Embalaje' : 'Package'} #{pkg.embajaIndex + 1}
                          </p>
                          {renderSuggestionGroup(pkg.fields)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            </>
            )}
          </div>
        );
      })}

      {/* Second: Participants (separate top-level section), grouped by export - Accept all / Revert all per export header */}
      {participantFields?.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between gap-2 mb-2 ml-3">
            <h3 className="font-semibold text-base text-gray-700 dark:text-gray-300 truncate min-w-0 flex-1"
              title={isSpanish ? 'Participantes' : 'Participants'}
            >
              {isSpanish ? 'Participantes' : 'Participants'}
            </h3>
            <button
              type="button"
              onClick={() => setIsParticipantsCollapsed((prev) => !prev)}
              className="flex items-center justify-center size-6 rounded-full text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 w-fit"
              title={isParticipantsCollapsed ? (isSpanish ? 'Expandir' : 'Expand') : (isSpanish ? 'Colapsar' : 'Collapse')}
            >
              <ChevronDown className={`size-4 transition-transform ${isParticipantsCollapsed ? '-rotate-90' : ''}`} />
            </button>
          </div>
          {!isParticipantsCollapsed && (() => {
            const byExport = participantFields.reduce((acc, s) => {
              const idx = s.declarationIndex ?? 0;
              if (!acc[idx]) acc[idx] = [];
              acc[idx].push(s);
              return acc;
            }, {});
            const sortedIndices = Object.keys(byExport).map(Number).sort((a, b) => a - b);
            return (
              <div className="space-y-4">
                {sortedIndices.map((declarationIndex) => {
                  const exportParticipantFields = byExport[declarationIndex] || [];
                  const exportPending = exportParticipantFields.filter((s) => s.status !== 'accepted' && s.status !== 'rejected');
                  const exportAccepted = exportParticipantFields.filter((s) => s.status === 'accepted');
                  const isExportCollapsed = reviewDetailsExportCollapsedByIndex[declarationIndex] === true;
                  return (
                    <div key={declarationIndex} className="mb-3">
                      <div className="flex items-center justify-between gap-2 mb-2 ml-3">
                        <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300 truncate min-w-0 flex-1">
                          {isSpanish ? 'Expediente' : 'Export'} #{declarationIndex + 1}
                        </h4>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {exportPending.length > 0 && (
                            <button
                              type="button"
                              onClick={() => handleAcceptAll(exportParticipantFields)}
                              className="truncate svg-force-white flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
                              title={isSpanish ? 'Aceptar todo' : 'Accept all'}
                            >
                              <Check className="size-3.5" />
                              <p className="truncate">{isSpanish ? 'Aceptar todo' : 'Accept all'}</p>
                            </button>
                          )}
                          {exportAccepted.some(hasRevertableOldValue) && (
                            <button
                              type="button"
                              onClick={() => handleRetryAll(exportParticipantFields)}
                              className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                              title={isSpanish ? 'Revertir todo' : 'Revert all'}
                            >
                              <RotateCcw className="size-3.5" />
                              {exportPending.length === 0 && (
                                <span>{isSpanish ? 'Revertir todo' : 'Revert all'}</span>
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleReviewDetailsExportCollapsed(declarationIndex)}
                            className="flex items-center justify-center size-6 rounded-full text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 w-fit"
                            title={isExportCollapsed ? (isSpanish ? 'Expandir' : 'Expand') : (isSpanish ? 'Colapsar' : 'Collapse')}
                            aria-expanded={!isExportCollapsed}
                          >
                            {isExportCollapsed ? (
                              <ChevronRight className="size-4 text-gray-600" />
                            ) : (
                              <ChevronDown className="size-4 text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>
                      {!isExportCollapsed && renderSuggestionGroup(exportParticipantFields)}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default AiSuggestionsContainer;
